import { z } from "zod";

const MAX_DB_INT = 53687091200;

export enum PlanFeatureName {
  MAX_ALLOWED_CANVASES = "max_allowed_canvases",
  ANONYMOUS_ENABLED = "anonymous_enabled",
  CANVAS_ELEMENTS_LIMIT = "max_allowed_canvas_elements",
  APPLY_VIEWER_RESTRICTED = "apply_viewer_restricted",
  CREATE_TEMPLATE_ENABLED = "create_template_enabled",
  GANTT_MAX_TASKS = "gantt_max_tasks",
  MONDAY_INTEGRATION_LIMIT = "monday_integration_limit",
  PREMIUM_EXPORT = "premium_export",
  VIDEO_CHAT = "video_chat",
  ALL_TEMPLATES_ENABLED = "all_templates_enabled",
  ORGCHART_MAX_NODES = "orgchart_max_nodes",
  ORGCHART_ADD_NEW_FIELDS = "orgchart_add_new_fields",
  PREMIUM_SHAPES = "premium_shapes",
}

export type PlanFeatureTypes = {
  [PlanFeatureName.MAX_ALLOWED_CANVASES]: number;
  [PlanFeatureName.ANONYMOUS_ENABLED]: boolean;
  [PlanFeatureName.CANVAS_ELEMENTS_LIMIT]: number;
  [PlanFeatureName.APPLY_VIEWER_RESTRICTED]: boolean;
  [PlanFeatureName.CREATE_TEMPLATE_ENABLED]: boolean;
  [PlanFeatureName.GANTT_MAX_TASKS]: number;
  [PlanFeatureName.MONDAY_INTEGRATION_LIMIT]: number;
  [PlanFeatureName.PREMIUM_EXPORT]: boolean;
  [PlanFeatureName.VIDEO_CHAT]: boolean;
  [PlanFeatureName.ALL_TEMPLATES_ENABLED]: boolean;
  [PlanFeatureName.ORGCHART_MAX_NODES]: number;
  [PlanFeatureName.ORGCHART_ADD_NEW_FIELDS]: boolean;
  [PlanFeatureName.PREMIUM_SHAPES]: boolean;
};

// Get a sample value to determine the expected type
type TypeMap = {
  boolean: boolean;
  number: number;
  string: string;
  object: object;
};

export const FEATURE_TYPE_MAP: Record<PlanFeatureName, keyof TypeMap> = {
  [PlanFeatureName.MAX_ALLOWED_CANVASES]: "number",
  [PlanFeatureName.ANONYMOUS_ENABLED]: "boolean",
  [PlanFeatureName.CANVAS_ELEMENTS_LIMIT]: "number",
  [PlanFeatureName.APPLY_VIEWER_RESTRICTED]: "boolean",
  [PlanFeatureName.CREATE_TEMPLATE_ENABLED]: "boolean",
  [PlanFeatureName.GANTT_MAX_TASKS]: "number",
  [PlanFeatureName.MONDAY_INTEGRATION_LIMIT]: "number",
  [PlanFeatureName.PREMIUM_EXPORT]: "boolean",
  [PlanFeatureName.VIDEO_CHAT]: "boolean",
  [PlanFeatureName.ALL_TEMPLATES_ENABLED]: "boolean",
  [PlanFeatureName.ORGCHART_MAX_NODES]: "number",
  [PlanFeatureName.ORGCHART_ADD_NEW_FIELDS]: "boolean",
  [PlanFeatureName.PREMIUM_SHAPES]: "boolean",
};

export const PLAN_FEATURES_DEFAULT_VALUES = {
  [PlanFeatureName.GANTT_MAX_TASKS]: MAX_DB_INT,
  [PlanFeatureName.MONDAY_INTEGRATION_LIMIT]: MAX_DB_INT,
  [PlanFeatureName.ORGCHART_MAX_NODES]: MAX_DB_INT,
  [PlanFeatureName.MAX_ALLOWED_CANVASES]: MAX_DB_INT,
  [PlanFeatureName.ANONYMOUS_ENABLED]: true,
  [PlanFeatureName.CANVAS_ELEMENTS_LIMIT]: MAX_DB_INT,
  [PlanFeatureName.APPLY_VIEWER_RESTRICTED]: true,
  [PlanFeatureName.CREATE_TEMPLATE_ENABLED]: true,
  [PlanFeatureName.PREMIUM_EXPORT]: true,
  [PlanFeatureName.VIDEO_CHAT]: true,
  [PlanFeatureName.ALL_TEMPLATES_ENABLED]: true,
  [PlanFeatureName.ORGCHART_ADD_NEW_FIELDS]: true,
  [PlanFeatureName.PREMIUM_SHAPES]: true,
};

export const planFeatures = z.object({
  name: z.string(),
  feature_value: z.string().nullish(),
});

export type PlanFeature = z.infer<typeof planFeatures>;
